function normalizeName(name) {
  if (!name) return '';
  return String(name)
    .toLowerCase()
    .trim()
    .replace(/[.'’]/g, '')
    .replace(/\s+/g, ' ');
}

function nameTokens(name) {
  return normalizeName(name)
    .split(' ')
    .filter((token) => token.length >= 2);
}

function levenshteinDistance(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const rows = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i += 1) {
    let previous = rows[0];
    rows[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const temp = rows[j];
      rows[j] =
        a[i - 1] === b[j - 1]
          ? previous
          : 1 + Math.min(previous, rows[j], rows[j - 1]);
      previous = temp;
    }
  }
  return rows[b.length];
}

function fuzzyNameSimilarity(a, b) {
  const left = normalizeName(a);
  const right = normalizeName(b);
  if (!left || !right) return 0;
  if (left === right) return 1;
  if (left.includes(right) || right.includes(left)) return 0.95;
  const distance = levenshteinDistance(left, right);
  const maxLen = Math.max(left.length, right.length);
  if (maxLen < 3) return 0;
  return 1 - distance / maxLen;
}

/**
 * Rank doctor patients by how strongly their name appears in the transcript.
 * Returns { best, candidates } where best is confident single match or null.
 */
export function matchDoctorPatientFromTranscript(patients, transcript) {
  const text = normalizeName(transcript || '');
  if (!text || !patients?.length) {
    return { best: null, candidates: [] };
  }

  const scored = patients
    .map((patient) => {
      const full = normalizeName(patient.full_name);
      const tokens = nameTokens(patient.full_name);
      let score = 0;

      if (full && text.includes(full)) score += 100;

      for (const token of tokens) {
        if (token.length < 3) continue;
        if (text.includes(token)) score += token.length >= 5 ? 40 : 25;
        else {
          // Fuzzy against transcript words
          const words = text.split(' ').filter((w) => w.length >= 3);
          let best = 0;
          for (const word of words) {
            best = Math.max(best, fuzzyNameSimilarity(token, word));
          }
          if (best >= 0.88) score += 30;
          else if (best >= 0.8) score += 15;
        }
      }

      // MRN mention
      if (patient.medical_record_number) {
        const mrn = String(patient.medical_record_number).toLowerCase();
        if (mrn.length >= 3 && text.includes(mrn)) score += 50;
      }

      return { patient, score };
    })
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score);

  const candidates = scored.map((row) => row.patient);
  const top = scored[0];
  const second = scored[1];

  if (!top || top.score < 40) {
    return { best: null, candidates };
  }
  if (second && top.score - second.score < 15 && second.score >= 40) {
    return { best: null, candidates: scored.filter((r) => r.score >= 40).map((r) => r.patient) };
  }
  return { best: top.patient, candidates };
}
